/**
 * The evals layer's hooks (task 1180): criteria at two scopes, the scores on a
 * result, and named suites.
 *
 * Criteria are named after what the Run Explorer already shows: **checks**
 * (deterministic) and **graded criteria** (model-graded). A test criterion is
 * listed and created under its test; a **service criterion** under the
 * endpoint; both are edited and deleted by id.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  endpointTestCriteriaApi,
  endpointTestRunsApi,
  endpointTestsApi,
  endpointTestSuitesApi,
} from '@/services/api';
import type {
  CreateEndpointTestCriterionInput,
  CreateEndpointTestSuiteInput,
  UpdateEndpointTestCriterionInput,
  UpdateEndpointTestSuiteInput,
} from '@/types/endpointTests';

/** Where a set of criteria lives. */
export type CriteriaScope =
  | { kind: 'test'; endpointTestId: string }
  | { kind: 'service'; endpointId: string };

export const criteriaQueryKey = (scope: CriteriaScope) =>
  scope.kind === 'test'
    ? (['endpoint-test-criteria', 'test', scope.endpointTestId] as const)
    : (['endpoint-test-criteria', 'service', scope.endpointId] as const);

export const useCriteria = (scope: CriteriaScope | null) =>
  useQuery({
    queryKey: scope ? criteriaQueryKey(scope) : ['endpoint-test-criteria', 'none'],
    queryFn: () =>
      (scope!.kind === 'test'
        ? endpointTestsApi.listCriteria(scope!.endpointTestId)
        : endpointTestCriteriaApi.listForService(scope!.endpointId)
      ).then((res) => res.data.data ?? []),
    enabled: !!scope,
  });

export const useCreateCriterion = (scope: CriteriaScope) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEndpointTestCriterionInput) =>
      (scope.kind === 'test'
        ? endpointTestsApi.createCriterion(scope.endpointTestId, data)
        : endpointTestCriteriaApi.createForService(scope.endpointId, data)
      ).then((res) => res.data.data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: criteriaQueryKey(scope) }),
  });
};

export const useUpdateCriterion = (scope: CriteriaScope) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEndpointTestCriterionInput }) =>
      endpointTestCriteriaApi.update(id, data).then((res) => res.data.data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: criteriaQueryKey(scope) }),
  });
};

export const useDeleteCriterion = (scope: CriteriaScope) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => endpointTestCriteriaApi.delete(id).then((res) => res.data.data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: criteriaQueryKey(scope) }),
  });
};

/** Create criteria for a test that was just created (the Add test form). */
export const useCreateTestCriteria = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ endpointTestId, criteria }: {
      endpointTestId: string;
      criteria: CreateEndpointTestCriterionInput[];
    }) => {
      for (const criterion of criteria) {
        await endpointTestsApi.createCriterion(endpointTestId, criterion);
      }
    },
    onSuccess: (_void, variables) =>
      void queryClient.invalidateQueries({
        queryKey: criteriaQueryKey({ kind: 'test', endpointTestId: variables.endpointTestId }),
      }),
  });
};

/** Every score on one result — checks, graded criteria and the human verdict. */
export const useEndpointTestRunScores = (endpointTestRunId: string | undefined, revision = '') =>
  useQuery({
    queryKey: ['endpoint-test-run-scores', endpointTestRunId, revision],
    queryFn: () => endpointTestRunsApi.listScores(endpointTestRunId!).then((res) => res.data.data ?? []),
    enabled: !!endpointTestRunId,
  });

// ─── Named suites ────────────────────────────────────────────────────────────

export const suitesQueryKey = (endpointId: string | undefined) => ['endpoint-test-suites', endpointId] as const;

export const useEndpointTestSuites = (endpointId: string | undefined) =>
  useQuery({
    queryKey: suitesQueryKey(endpointId),
    queryFn: () => endpointTestSuitesApi.list(endpointId!).then((res) => res.data.data ?? []),
    enabled: !!endpointId,
  });

export const useCreateEndpointTestSuite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEndpointTestSuiteInput) =>
      endpointTestSuitesApi.create(data).then((res) => res.data.data),
    onSuccess: (_suite, variables) =>
      void queryClient.invalidateQueries({ queryKey: suitesQueryKey(variables.endpointId) }),
  });
};

export const useUpdateEndpointTestSuite = (endpointId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEndpointTestSuiteInput }) =>
      endpointTestSuitesApi.update(id, data).then((res) => res.data.data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: suitesQueryKey(endpointId) }),
  });
};

export const useDeleteEndpointTestSuite = (endpointId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => endpointTestSuitesApi.delete(id).then((res) => res.data.data),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: suitesQueryKey(endpointId) }),
  });
};
